import { apiMixins } from "./api";
import { MAX_FOLDER_DELETE_FILES } from "../util/featureFlags";

const FOLDER_ENDPOINT = "item/folder";
const GROUP_REGISTER_ENDPOINT = "group_register";

export const folderMixins = {
         mixins: [apiMixins],
         methods: {
           async createFolder(parentItem, name, defaultRetention) {
             const normalizedName = typeof name === "string" ? name.trim() : name;
             return await this.callAPI(FOLDER_ENDPOINT, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({
                 parentId: parentItem.id,
                 name: normalizedName,
                 defaultRetention,
               }),
             });
           },
           async createGroupRegister(parentItem, name, defaultRetention) {
             const normalizedName = typeof name === "string" ? name.trim() : name;
             return await this.callAPI(GROUP_REGISTER_ENDPOINT, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({
                 parentId: parentItem.id,
                 name: normalizedName,
                 defaultRetention
               }),
             });
           },
           async createGroupRegisterFolder(parentItem, name, defaultRetention) {
             const normalizedName = typeof name === "string" ? name.trim() : name;
             return await this.callAPI(`${GROUP_REGISTER_ENDPOINT}/${parentItem.id}`, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({
                 parentId: parentItem.id,
                 name: normalizedName,
                 defaultRetention,
               }),
             });
           },
           async forceCreateGroupRegisterFolder(parentItem, name, defaultRetention) {
             const normalizedName = typeof name === "string" ? name.trim() : name;
             return await this.callAPI(
               `${GROUP_REGISTER_ENDPOINT}/${parentItem.id}/force`,
               {
                 method: "POST",
                 headers: { "Content-Type": "application/json" },
                 body: JSON.stringify({ parentId: parentItem.id, name: normalizedName, defaultRetention }),
               }
             );
           },
           async getNextGroupRegisterPrimaryNumber(
             folderId,
             { showLoader = false } = {}
           ) {
             return await this.callAPI(
               `${GROUP_REGISTER_ENDPOINT}/${folderId}/next`,
               {
                 showLoader,
               }
             );
           },
           async getNextGroupRegisterSecondaryNumber(
             folderId,
             primaryNumber,
             { showLoader = false } = {}
           ) {
             return await this.callAPI(
               `${GROUP_REGISTER_ENDPOINT}/${folderId}/next/${primaryNumber}`,
               {
                 showLoader,
               }
             );
           },
           async getGroupRegisterPrimaryNumbers(
             folderId,
             { showLoader = false } = {}
           ) {
             return await this.callAPI(
               `${GROUP_REGISTER_ENDPOINT}/${folderId}/primary`,
               {
                 showLoader,
               }
             );
           },
           async createPaths(parentItem, paths, tags, metadata, pathEntries) {
             const body = {
               parentId: parentItem.id,
               tags: tags,
               metadata: metadata,
             };
             if (pathEntries && pathEntries.length) {
               body.pathEntries = pathEntries;
             } else {
               body.paths = paths;
             }
             const uploadJobContext = (pathEntries || []).some((entry) =>
               entry?.uploadJobId || entry?.uploadJobLogId || entry?.clientEntryId
             );
             return await this.callAPI(`${FOLDER_ENDPOINT}/paths`, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               showLoader: !uploadJobContext,
               body: JSON.stringify(body),
             });
           },
           async getFolder(
             id,
             {
               page = 0,
               pageSize = 0,
               sort,
               showLoader = true,
               filter = "",
               onlyFolders = false,
               flag = "standard",
             } = {}
           ) {
             const query = { page, pageSize, sort, filter, onlyFolders, flag };
             if (!sort) {
               delete query.sort;
             }
             return await this.callAPI(`${FOLDER_ENDPOINT}/${id}`, {
               method: "GET",
               query,
               showLoader,
             });
           },
           async getAllItems({
             page = 0,
             pageSize = 0,
             sort,
             showLoader = true,
             filter = "",
           } = {}) {
             return await this.callAPI(`item/shared`, {
               method: "GET",
               query: { page, pageSize, sort, filter },
               showLoader,
             });
           },
           async getDownloadItems({
             page = 0,
             pageSize = 0,
             sort,
             showLoader = true,
             filter = "",
           } = {}) {
             return await this.callAPI(`item/downloads`, {
               method: "GET",
               query: { page, pageSize, sort, filter },
               showLoader,
             });
           },
           async getFolderPath(id, { showLoader = true } = {}) {
             const response = await this.callAPI(
               `${FOLDER_ENDPOINT}/${id}/path`,
               { method: "GET", showLoader }
             );
             return response.paths;
           },
           async refreshIndex(itemId, { fast = false } = {}) {
             return await this.callAPI(`index/refresh`, {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ itemId, fast }),
               showLoader: false,
             });
           },
           async getRecursiveFolderProperties(id, { showLoader = true } = {}) {
             const response = await this.callAPI(
               `${FOLDER_ENDPOINT}/${id}/properties-summary`,
               { method: "GET", showLoader }
             );
             const legacyDetailsAvailable = response.legacyDetailsAvailable !== false;
             return {
               numberOfFiles: response.properties.numberOfFiles,
               numberOfFolders: legacyDetailsAvailable
                 ? response.properties.numberOfFolders ?? 0
                 : null,
               numberOfFilesOnly:
                 response.properties.numberOfFilesOnly ??
                 response.properties.numberOfFiles,
               totalSize: response.properties.totalSize,
               numberOfFilesNotUploaded:
                 legacyDetailsAvailable
                   ? response.properties.numberOfFilesNotUploaded ?? 0
                   : null,
               totalSizeNotUploaded:
                 legacyDetailsAvailable
                   ? response.properties.totalSizeNotUploaded ?? 0
                   : null,
               numberOfNonExpiredFiles:
                 response.properties.numberOfNonExpiredFiles,
               numberOfSuspendedFiles:
                 response.properties.numberOfSuspendedFiles,
               source: response.source,
               inventoryReportDate: response.inventoryReportDate,
               inventorySnapshotAt: response.inventorySnapshotAt,
               countsUpdatedAt: response.countsUpdatedAt,
               changedSinceSnapshot: response.changedSinceSnapshot,
               status: response.status,
               storedObjectCount: response.storedObjectCount,
               storedSizeBytes: response.storedSizeBytes,
               legacyDetailsAvailable,
             };
           },
           async calculateLiveFolderProperties(id, { showLoader = true } = {}) {
             const response = await this.callAPI(
               `${FOLDER_ENDPOINT}/${id}/properties-summary/live`,
               { method: "POST", showLoader }
             );
             return {
               ...response.properties,
               source: response.source,
               countsUpdatedAt: response.countsUpdatedAt,
               changedSinceSnapshot: false,
               legacyDetailsAvailable: true,
             };
           },
           async renameFolder(item, name) {
             await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
               method: "PATCH",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ name }),
             });
           },
           async moveFolder(item, parentItem) {
             await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
               method: "PATCH",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ parentId: parentItem.id }),
             });
           },
          async deleteFolder(item) {
            await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
              method: "DELETE",
            });
          },
          async evaluateFolderDeleteGate(id) {
            const response = await this.callAPI(`${FOLDER_ENDPOINT}/${id}/recursive`, {
              method: "GET",
              showLoader: false,
            });
            const totalItems = response?.properties?.numberOfFiles ?? 0;
            const maxFiles = this.getMaxFolderDeleteFiles();
            const isTooManyItems = maxFiles > 0 && totalItems > maxFiles;
            return { totalItems, isTooManyItems };
          },
          getMaxFolderDeleteFiles() {
            return Number.isFinite(MAX_FOLDER_DELETE_FILES) ? MAX_FOLDER_DELETE_FILES : 1000000;
          },
           async setFolderSteward(item, steward) {
             await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
               method: "PATCH",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({ steward }),
             });
           },
           async uploadFromGcs(folderId, data) {
      await this.callAPI(`${FOLDER_ENDPOINT}/${folderId}/gcs-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    },

           async updateFolder(
             item,
             { suspensionOrders, steward, stewardPropagation, owner, ownerPropagation, tags, metadata, tagsPropagation, metadataPropagation, groupRegister, defaultRetention, defaultRetentionPropagation, defaultUploadStorageClass, groupRegisterPrimaryIncrement, isSecret, immutable }
           ) {
             let suspensionOrdersList = null;
             if (suspensionOrders || suspensionOrders === "") {
               suspensionOrdersList =
                 suspensionOrders.length === 0
                   ? []
                   : suspensionOrders.split(",");
             }
             const body = {
               suspensionOrders: suspensionOrdersList,
               steward,
               stewardPropagation,
               owner,
               ownerPropagation,
               tags,
               metadata,
               tagsPropagation,
               metadataPropagation,
               groupRegister,
               isSecret,
               immutable,
               defaultUploadStorageClass,
               defaultRetentionPropagation,
               groupRegisterPrimaryIncrement
             };
             if (Object.prototype.hasOwnProperty.call(arguments[1], "defaultRetention")) {
               body.defaultRetention = defaultRetention ?? "";
             }
             const patchResponse = await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
               method: "PATCH",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify(body),
             });
             if (patchResponse && patchResponse.jobId) {
               return patchResponse;
             }
             try {
               const response = await this.callAPI(`${FOLDER_ENDPOINT}/${item.id}`, {
                 method: "GET",
                 showLoader: false,
               });
               return response?.self || null;
             } catch (error) {
               if (error?.code === "ACCESS_DENIED") {
                 return null;
               }
               throw error;
             }
           },
         async touchFolder(folderId) {
             await this.callAPI(`${FOLDER_ENDPOINT}/${folderId}/touch`, {
               method: "POST",
               showLoader: false,
             });
           },
         },
       };
